/**
 * Component Name: SupplierGroupedTable
 * Description: A table component that groups invoices by supplier and displays aggregated data with sortable columns and clickable supplier names.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to group by suppliers.
 *   - handleSupplierClick (function): Callback to handle clicks on supplier names for drill-down.
 * Feature: Displays supplier-level invoice data, including total invoice amounts, subtotals, and PO counts, with clickable supplier names to view associated invoices in the invoice management interface.
 */
import React from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import { GSTORVAT } from '../../util/enums';
import { INVOICE_CSS_CLASSES, INVOICE_ERROR_MESSAGES, INVOICE_TABLE_COLUMNS } from '../../util/enums/invoice-management';
const SupplierGroupedTable = ({ filteredInvoices, handleSupplierClick }) => {
    const groupedData = [];
    const supplierMap = new Map();

    filteredInvoices.forEach((inv) => {
        const supplierId = inv.PurchaseOrders?.vendorId;
        const supplierName = inv.supplier || INVOICE_ERROR_MESSAGES.UNKNOWN_SUPPLIER;

        if (supplierId && !supplierMap.has(supplierId)) {
            supplierMap.set(supplierId, {
                supplierId,
                supplierName,
                totalInvoiceAmount: toDecimal(0),
                totalInvoiceSubtotal: toDecimal(0),
                invoiceCount: 0,
                poTotal: toDecimal(0),
                uniquePOIds: new Set(), // Track unique PO IDs instead of count
            });
        }
        if (supplierId) {
            const supplier = supplierMap.get(supplierId);
            supplier.totalInvoiceAmount = supplier.totalInvoiceAmount.plus(toDecimal(inv.totalAmount || 0));
            supplier.totalInvoiceSubtotal = supplier.totalInvoiceSubtotal.plus(toDecimal(inv.subTotal || 0));
            supplier.invoiceCount += 1;
            supplier.poTotal = supplier.poTotal.plus(toDecimal(inv.PurchaseOrders?.total || 0));

            // Track unique PO ID instead of counting each invoice
            if (inv.PurchaseOrders?.id) {
                supplier.uniquePOIds.add(inv.PurchaseOrders.id);
            }
        }
    });

    supplierMap.forEach((value) => {
        // Convert Decimal values to numbers for display
        value.totalInvoiceAmount = roundValue(value.totalInvoiceAmount);
        value.totalInvoiceSubtotal = roundValue(value.totalInvoiceSubtotal);
        value.poTotal = roundValue(value.poTotal);

        // Convert uniquePOIds Set to poCount for display
        if (value.uniquePOIds) {
            value.poCount = value.uniquePOIds.size;
            delete value.uniquePOIds; // Remove the Set from the final data
        }

        groupedData.push(value);
    });
    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.SUPPLIER,
            field: 'supplierName',
            render: (row) => (
                <span className={INVOICE_CSS_CLASSES.SUPPLIER_LINK} onClick={() => handleSupplierClick(row.supplierId, row.supplierName)}>
                    {row.supplierName}
                </span>
            ),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.TOTAL_INVOICE_AMOUNT,
            field: 'totalInvoiceAmount',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.TOTAL_INVOICE_AMOUNT}>{toUSD(row.totalInvoiceAmount)}</span>,
            sortable: true,
        },
        {
            title: `${INVOICE_TABLE_COLUMNS.INVOICE_SUBTOTAL} (Ex. ${GSTORVAT})`,
            field: 'totalInvoiceSubtotal',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.totalInvoiceSubtotal)}</span>,
            sortable: true
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
            field: 'invoiceCount',
            class: 'text-right',
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_TOTAL,
            field: 'poTotal',
            class: 'text-right',
            render: (row) => toUSD(row.poTotal),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_COUNT,
            field: 'poCount',
            class: 'text-right',
            render: (row) => <span>{row.poCount}</span>,
            sortable: true,
        },
    ];

    const tableItemClickHandler = (row) => {
        handleSupplierClick(row.supplierId, row.supplierName);
    };

    return (
        <div className="table-container">
            <CustomTable columns={columns} data={groupedData} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default SupplierGroupedTable;